import { useEffect } from "react";

export interface SeoMeta {
  title: string;
  description?: string;
  canonicalPath?: string;
  ogType?: "website" | "article";
  ogImage?: string;
  jsonLd?: Record<string, unknown> | null;
  publishedTime?: string;
}

const setMeta = (
  selector: string,
  attribute: "name" | "property",
  attrValue: string,
  content: string
): (() => void) => {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  let created = false;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, attrValue);
    document.head.appendChild(tag);
    created = true;
  }
  const previous = tag.content;
  tag.content = content;
  return () => {
    if (created && tag) tag.remove();
    else if (tag) tag.content = previous;
  };
};

const setLink = (rel: string, href: string): (() => void) => {
  let tag = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  let created = false;
  if (!tag) {
    tag = document.createElement("link");
    tag.rel = rel;
    document.head.appendChild(tag);
    created = true;
  }
  const previous = tag.href;
  tag.href = href;
  return () => {
    if (created && tag) tag.remove();
    else if (tag) tag.href = previous;
  };
};

const setJsonLd = (id: string, payload: Record<string, unknown>): (() => void) => {
  const existing = document.head.querySelector<HTMLScriptElement>(`script[data-seo-id="${id}"]`);
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.dataset.seoId = id;
  script.text = JSON.stringify(payload).replace(/</g, "\\u003c");
  document.head.appendChild(script);
  return () => {
    script.remove();
  };
};

/**
 * Sincroniza title, description, canonical, OpenGraph/Twitter e JSON-LD com a rota atual.
 * Restaura o estado anterior ao desmontar.
 */
export function useSeoMeta(meta: SeoMeta): void {
  useEffect(() => {
    const cleanups: Array<() => void> = [];
    const previousTitle = document.title;
    document.title = meta.title;
    cleanups.push(() => {
      document.title = previousTitle;
    });

    if (meta.description) {
      cleanups.push(
        setMeta('meta[name="description"]', "name", "description", meta.description)
      );
      cleanups.push(
        setMeta('meta[property="og:description"]', "property", "og:description", meta.description)
      );
      cleanups.push(
        setMeta(
          'meta[property="twitter:description"]',
          "property",
          "twitter:description",
          meta.description
        )
      );
    }

    cleanups.push(setMeta('meta[property="og:title"]', "property", "og:title", meta.title));
    cleanups.push(
      setMeta('meta[property="twitter:title"]', "property", "twitter:title", meta.title)
    );

    if (meta.ogType) {
      cleanups.push(setMeta('meta[property="og:type"]', "property", "og:type", meta.ogType));
    }

    if (meta.canonicalPath) {
      const url = new URL(meta.canonicalPath, window.location.origin).href;
      cleanups.push(setLink("canonical", url));
      cleanups.push(setMeta('meta[property="og:url"]', "property", "og:url", url));
      cleanups.push(setMeta('meta[property="twitter:url"]', "property", "twitter:url", url));
    }

    if (meta.publishedTime) {
      cleanups.push(
        setMeta(
          'meta[property="article:published_time"]',
          "property",
          "article:published_time",
          meta.publishedTime
        )
      );
    }

    if (meta.ogImage) {
      const imageUrl = new URL(meta.ogImage, window.location.origin).href;
      cleanups.push(
        setMeta('meta[property="og:image"]', "property", "og:image", imageUrl)
      );
      cleanups.push(
        setMeta(
          'meta[property="twitter:image"]',
          "property",
          "twitter:image",
          imageUrl
        )
      );
    }

    if (meta.jsonLd) {
      cleanups.push(setJsonLd("page", meta.jsonLd));
    }

    return () => {
      for (const fn of cleanups.reverse()) fn();
    };
  }, [
    meta.title,
    meta.description,
    meta.canonicalPath,
    meta.ogType,
    meta.ogImage,
    meta.publishedTime,
    meta.jsonLd,
  ]);
}

/**
 * Remove tags markdown e devolve uma descrição plana adequada para meta description.
 */
export function summaryToDescription(markdown: string, max = 155): string {
  const cleaned = markdown
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/(?<!\*)\*(?!\*)([^*]+)\*(?!\*)/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/~~([^~]+)~~/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
  if (cleaned.length <= max) return cleaned;
  const slice = cleaned.slice(0, max - 1);
  const lastSpace = slice.lastIndexOf(" ");
  return (lastSpace > max * 0.6 ? slice.slice(0, lastSpace) : slice).trimEnd() + "…";
}
